import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Update } from '@ngrx/entity'
import { Action } from '@ngrx/store'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { GbCompleteProfileDialogComponent } from 'libs/gb-front/auth/util-auth-dialog/src/lib/gb-complete-profile-dialog/gb-complete-profile-dialog.component'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { gbCheckLoaded } from 'libs/shared/utils/operators/src/lib/gb-check-loaded/gb-check-loaded.operator'
import {
  catchError,
  combineLatest,
  exhaustMap,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
  zip,
} from 'rxjs'
import { SelectedProfileApiService } from './selected-profile-api.service'
import { SelectedProfileFacade } from './selected-profile.facade'
import { CustomValuesEntity, ProfileEntity } from './selected-profile.models'

import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import * as ItemDispatchesActions from 'libs/gb-front/checkout/data-access-item-dispatches/src/lib/+state/item-dispatches.actions'
import * as InvoiceActions from 'libs/gb-front/checkout/data-access/src/lib/+state/invoice.actions'
import * as ProfileItemActivitiesActions from 'libs/gb-front/item/data-access-profile-item-activities/src/lib/+state/profile-item-activities.actions'
import * as VouchersActions from 'libs/gb-front/item/data-access-vouchers/src/lib/+state/vouchers.actions'
import * as ItemActions from 'libs/gb-front/item/data-access/src/lib/+state/items.actions'
import * as CustomerPaymentMethodsActions from 'libs/gb-front/payment/data-access-customer-payment-methods/src/lib/+state/customer-payment-methods.actions'
import * as SelectedProfileActions from './selected-profile.actions'

@Injectable()
export class SelectedProfileEffects {
  setSelectedProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.setSelectedProfile),
      gbCheckLoaded(this.auctionFacade.loaded$),
      withLatestFrom(this.authFacade.isVolunteer$),
      filter(([, isVolunteer]) => isVolunteer),
      switchMap(([action]) => [SelectedProfileActions.syncProfile({}), ...(action.onSelectProfileActions || [])]),
    ),
  )

  clearSelectedProfileFieldsVolunteer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.clearSelectedProfileFieldsVolunteer),
      withLatestFrom(this.authFacade.isVolunteer$),
      filter(([, isVolunteer]) => isVolunteer),
      switchMap(() => [
        SelectedProfileActions.clearSelectedProfile(),
        ProfileItemActivitiesActions.clearProfileItemActivities(),
        VouchersActions.clearVouchers(),
        InvoiceActions.clearInvoice(),
        ItemDispatchesActions.clearItemDispatches(),
        CustomerPaymentMethodsActions.clearCustomerPaymentMethods(),
      ]),
    ),
  )

  syncProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.syncProfile),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.selectedProfileFacade.selectedProfileId$),
      tap(([action]) => {
        if (action.showLoader) this.loaderService.showLoaderDialog(LoaderActionTypeEnum.SyncProfile)
      }),
      switchMap(([action, auctionUrlName, selectedProfileId]) =>
        this.selectedProfileApiService.syncProfile(auctionUrlName, selectedProfileId).pipe(
          switchMap((profile) => {
            const actions: Action[] = [SelectedProfileActions.syncProfileSuccess({ profile })]

            if (action.checkProfile) {
              actions.push(SelectedProfileActions.checkProfile(action.onSuccess))
            } else if (action.onSuccess) {
              actions.push(action.onSuccess())
            }

            return actions
          }),
          catchError(async (error: HttpErrorResponse) =>
            SelectedProfileActions.syncProfileFailure({
              error,
              showSnackBar: action.showSnackBarOnFail && error.status !== 500,
            }),
          ),
        ),
      ),
    ),
  )

  hideSyncProfileLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedProfileActions.syncProfileSuccess, SelectedProfileActions.syncProfileFailure),
        map(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.SyncProfile)),
      ),
    { dispatch: false },
  )

  showSnackbarOnSyncProfileFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedProfileActions.syncProfileFailure),
        map((action) => {
          if (action.showSnackBar) {
            let errorMessage = this.errorMessageService.getErrorMessage(action.error.error)
            const messageBody = errorMessage ? errorMessage : 'ERROR_GENERIC'
            return this.notificationService.error(messageBody)
          }
        }),
      ),
    { dispatch: false },
  )

  /**
   * "Registration additional fields" and "My account > Account details" profile update.
   * payload = {
   *  profile: ProfileEntity // form value with all fields to be updated
   *  showSnackBar: boolean // true by default, after reg should only show for profile update
   *  snackBarMessage: string
   *  dialog: boolean
   *  shouldRedirect: boolean // if should redirect to reg success
   * }
   */
  updateSelectedProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.updateSelectedProfile),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.selectedProfileFacade.selectedProfileId$),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.ProfileUpdate, 'LOADER.SAVING')),
      switchMap(([payload, auctionUrlName, selectedProfileId]) => {
        return this.selectedProfileApiService.updateProfile(auctionUrlName, payload.profile, selectedProfileId).pipe(
          map((profile) =>
            SelectedProfileActions.updateSelectedProfileSuccess({
              profile,
              showSnackBar: payload.showSnackBar,
              snackBarMessage: payload.snackBarMessage,
              dialog: payload.dialog,
              shouldRedirect: payload.shouldRedirect,
            }),
          ),
          catchError(async (error: HttpErrorResponse) =>
            SelectedProfileActions.updateSelectedProfileFailure({ error }),
          ),
        )
      }),
    ),
  )

  updateAuthProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SelectedProfileActions.updateSelectedProfileSuccess,
        SelectedProfileActions.setCompleteProfilePromptedSuccess,
      ),
      withLatestFrom(this.authFacade.isGuest$),
      filter(([, isGuest]) => isGuest),
      map(([action]) => {
        const update: Update<ProfileEntity> = {
          id: action.profile.uuid,
          changes: action.profile,
        }
        return AuthActions.updateAuthProfile({ update })
      }),
    ),
  )

  hideUpdateProfileLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SelectedProfileActions.updateSelectedProfileSuccess,
          SelectedProfileActions.updateSelectedProfileFailure,
        ),
        map(() => {
          this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.ProfileUpdate)
        }),
      ),
    { dispatch: false },
  )

  routeOnUpdateSelectedProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedProfileActions.updateSelectedProfileSuccess),
        withLatestFrom(this.auctionFacade.auctionUrlName$),
        tap(([action, urlName]) => {
          if (action.dialog) return
          if (action.shouldRedirect) {
            this.router.navigate([urlName, 'register-success'], {
              queryParams: { complete: true },
            })
          }
        }),
      ),
    { dispatch: false },
  )

  closeDialogOnUpdateSelectedProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedProfileActions.updateSelectedProfileSuccess),
        map((action) => {
          if (action.dialog) {
            this.gbFrontDialogService.closeDialog(GbCompleteProfileDialogComponent, true)
          }
        }),
      ),
    { dispatch: false },
  )

  showSnackbarOnUpdateSelectedProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedProfileActions.updateSelectedProfileSuccess),
        map((payload) => {
          if (payload.showSnackBar) {
            const message = payload.snackBarMessage ? payload.snackBarMessage : 'PROFILE_UPDATE_SUCCESS'
            this.notificationService.success(message)
          }
        }),
      ),
    { dispatch: false },
  )

  showSnackbarOnUpdateSelectedProfileFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedProfileActions.updateSelectedProfileFailure),
        map((action) => {
          const errorMessage = this.errorMessageService.getErrorMessage(action.error.error)
          const messageBody = errorMessage ? errorMessage : 'ERROR_GENERIC'
          return this.notificationService.error(messageBody)
        }),
      ),
    { dispatch: false },
  )

  setCompleteProfilePrompted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.setCompleteProfilePrompted),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.selectedProfileFacade.selectedProfileId$),
      switchMap(([, auctionUrlName, selectedProfileId]) =>
        this.selectedProfileApiService
          .updateProfile(auctionUrlName, { complete_profile_prompted: true }, selectedProfileId)
          .pipe(
            map((profile) => SelectedProfileActions.setCompleteProfilePromptedSuccess({ profile })),
            catchError(async (error: HttpErrorResponse) =>
              SelectedProfileActions.updateSelectedProfileFailure({ error }),
            ),
          ),
      ),
    ),
  )

  checkProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.checkProfile),
      gbCheckLoaded(
        combineLatest(
          [this.selectedProfileFacade.loaded$, this.selectedProfileFacade.selectedProfile$],
          (profilesLoaded, selectedProfile) => profilesLoaded && !!selectedProfile,
        ),
        () => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.CheckProfile, 'LOADING'),
        () => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.CheckProfile),
      ),
      withLatestFrom(this.selectedProfileFacade.selectedProfile$),
      switchMap(([action, selectedProfile]) => {
        const actions: Action[] = []

        if (
          selectedProfile?.additional_info_required ||
          !selectedProfile?.complete_profile_prompted ||
          selectedProfile?.password_required
        ) {
          actions.push(SelectedProfileActions.openCheckProfileDialog(action.onSuccess))
        } else if (action.onSuccess) {
          actions.push(action.onSuccess())
        }

        return actions
      }),
    ),
  )

  openCheckProfileDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.openCheckProfileDialog),
      exhaustMap((action) => {
        return zip([
          of(action),
          this.gbFrontDialogService.getDialogObs(GbCompleteProfileDialogComponent, { data: action.formConfig }),
        ])
      }),
      switchMap(([action, result]) => {
        const actions: Action[] = []

        if (result && action.onSuccess) {
          actions.push(action.onSuccess())
        }

        return actions
      }),
    ),
  )

  createCustomValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.createCustomValue),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.selectedProfileFacade.selectedProfileId$),
      mergeMap(([action, urlName, selectedProfileId]) =>
        this.selectedProfileApiService
          .createCustomValue({
            auction: urlName,
            custom_field: action.customFieldUuid,
            profile: selectedProfileId,
            value: action.value,
          })
          .pipe(
            map((response) =>
              SelectedProfileActions.createCustomValueSuccess({
                customValue: response,
              }),
            ),
            catchError(async (error) => SelectedProfileActions.createCustomValueFailure({ error })),
          ),
      ),
    ),
  )

  updateCustomValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.updateCustomValue),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.selectedProfileFacade.selectedProfileId$),
      mergeMap(([action, urlName, selectedProfileId]) =>
        this.selectedProfileApiService
          .updateCustomValue(<CustomValuesEntity>{
            ...{
              auction: urlName,
              profile: selectedProfileId,
            },
            ...action.update.changes,
          })
          .pipe(
            map((response) =>
              SelectedProfileActions.updateCustomValueSuccess({
                customValue: response,
              }),
            ),
            catchError(async (error) => SelectedProfileActions.updateCustomValueFailure({ error })),
          ),
      ),
    ),
  )

  updateProfileNotificationSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedProfileActions.updateProfileNotificationSettings),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.selectedProfileFacade.selectedProfile$),
      switchMap(([action, auctionUrlName, selectedProfile]) => {
        const actions: Action[] = []
        const snackBarMessage = 'GENERAL_SETTINGS.SETTINGS_SAVED_SUCCESS'
        const updatePoppers = action.payload.showPoppers !== undefined
        const updateIsSubscribed = action.payload.isSubscribed !== undefined

        if (updatePoppers) {
          actions.push(
            ItemActions.showPoppers({
              showPoppers: !!action.payload.showPoppers,
              snackBarMessage: !updateIsSubscribed ? snackBarMessage : undefined,
            }),
          )
        }
        if (updateIsSubscribed) {
          actions.push(
            SelectedProfileActions.updateSelectedProfile({
              profile: <ProfileEntity>{
                ...selectedProfile,
                announcement_notifications_option: action.payload.isSubscribed,
                outbid_notifications_option: action.payload.isSubscribed,
              },
              showSnackBar: true,
              snackBarMessage,
              shouldRedirect: false,
              dialog: false,
            }),
          )
        }
        return actions
      }),
    ),
  )

  constructor(
    private readonly actions$: Actions,
    private selectedProfileApiService: SelectedProfileApiService,
    private selectedProfileFacade: SelectedProfileFacade,
    private authFacade: AuthFacade,
    private auctionFacade: AuctionFacade,
    private gbFrontDialogService: GbFrontDialogService,
    private loaderService: GbLoaderService,
    private notificationService: GbNotificationService,
    private errorMessageService: GbErrorMessageService,
    private router: Router,
  ) {}
}
